import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom'; // ✅ Added for breadcrumbs
import { listBlogs, createBlog, deleteBlog, updateBlog } from 'src/actions/blogActions';
import { getAccountStatus } from 'src/actions/businessModuleActions';
import { imgUrl } from 'src/actions/types';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './MyBlogs.css';
import MessageModal from '../Pages/MessageModel'; // Adjust path if necessary
const MyBlogs = ({ 
  blogState, 
  listBlogs, 
  createBlog, 
  deleteBlog, 
  updateBlog,
  getAccountStatus,
  accountStatus 
}) => {
  // --- STATE MANAGEMENT ---
  const [filters, setFilters] = useState({ search: '', status: 'All', sortBy: 'date_desc' });
  const [showAdd, setShowAdd] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', content: '', image: null });
  const [addPreview, setAddPreview] = useState(null);
  const [editForm, setEditForm] = useState({ id: '', title: '', content: '', image: null, status: '' });
  const [editPreview, setEditPreview] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, id: null });
  const { blogs = [], loading, error, success } = blogState;

  const [hasBlogAccess, setHasBlogAccess] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  // --- ACCESS CONTROL LOGIC ---
  const checkBlogAddonAccess = async () => {
    try {
      const hospitalId = localStorage.getItem('hospitalCode');
      const userRole = localStorage.getItem('userRole');
      
      if (userRole?.toLowerCase() !== 'admin') {
        setHasBlogAccess(true);
        setSubscriptionChecked(true);
        setCheckingAccess(false);
        return;
      }

      if (!hospitalId) {
        setHasBlogAccess(false);
        setSubscriptionChecked(true);
        setCheckingAccess(false);
        return;
      }

      const accountStatus = await getAccountStatus(hospitalId);
      const hasBlogModule = accountStatus.activeModules.some(module => 
        module.module.toLowerCase().includes('blog')
      );
      
      setHasBlogAccess(hasBlogModule);
      setSubscriptionChecked(true);
      setCheckingAccess(false);
      
    } catch (error) {
      const userRole = localStorage.getItem('userRole');
      setHasBlogAccess(userRole?.toLowerCase() !== 'admin');
      setSubscriptionChecked(true);
      setCheckingAccess(false);
    }
  };

  useEffect(() => {
    checkBlogAddonAccess();
  }, []);

  useEffect(() => {
    if (hasBlogAccess && !checkingAccess) {
      const hospitalId = localStorage.getItem('hospitalCode');
      if (hospitalId) {
        listBlogs({ hospitalID: Number(hospitalId) });
      }
    }
  }, [listBlogs, hasBlogAccess, checkingAccess]);

  useEffect(() => {
    if (success && hasBlogAccess) {
      setSubmitted(true);
      setShowAdd(false);
      setShowEdit(false);
      setAddForm({ title: '', content: '', image: null });
      setAddPreview(null);
      setEditForm({ id: '', title: '', content: '', image: null, status: '' });
      setEditPreview(null);
      fetchBlogs();
      setTimeout(() => setSubmitted(false), 3000);
    }
  }, [success, hasBlogAccess]);

  const fetchBlogs = () => {
    const hospitalId = localStorage.getItem('hospitalCode');
    if (hospitalId && hasBlogAccess) {
      listBlogs({ hospitalID: Number(hospitalId) });
    }
  };

  // --- FILTERING & SORTING ---
  const filteredBlogs = useMemo(() => {
    if (!hasBlogAccess) return [];
    
    let filtered = blogs;
    if (filters.status !== 'All') {
      filtered = filtered.filter(blog => blog.status === filters.status);
    }
    if (filters.search.trim()) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter(({ title, content }) =>
        (title?.toLowerCase().includes(s) || content?.toLowerCase().includes(s))
      );
    }
    filtered = [...filtered];
    switch (filters.sortBy) {
      case 'date_asc':
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'date_desc':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'title_asc':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title_desc':
        filtered.sort((a, b) => b.title.localeCompare(a.title));
        break;
      default:
        break;
    }
    return filtered;
  }, [blogs, filters, hasBlogAccess]);

  // --- HANDLERS ---
  const onAddInputChange = e => {
    const { name, value, files } = e.target;
    if (name === 'image' && files && files.length > 0) {
      setAddForm(prev => ({ ...prev, image: files[0] }));
      setAddPreview(URL.createObjectURL(files[0]));
    } else {
      setAddForm(prev => ({ ...prev, [name]: value }));
    }
  };
  // 1. Function to open the modal
const handleDeleteClick = (id) => {
  setDeleteModal({ show: true, id: id });
};

// 2. Function to confirm and execute deletion
const confirmDelete = () => {
  if (deleteModal.id) {
    deleteBlog(deleteModal.id).then(() => {
      fetchBlogs();
      setDeleteModal({ show: false, id: null });
    });
  }
};
  const onEditInputChange = e => {
    const { name, value, files } = e.target;
    if (name === 'image' && files && files.length > 0) {
      setEditForm(prev => ({ ...prev, image: files[0] }));
      setEditPreview(URL.createObjectURL(files[0]));
    } else {
      setEditForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddSubmit = e => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', addForm.title);
    fd.append('content', addForm.content);
    if (addForm.image) fd.append('image', addForm.image);
    fd.append('status', 'created');
    const hospitalId = localStorage.getItem('hospitalCode');
    if (hospitalId) fd.append('hospitalID', hospitalId);
    createBlog(fd);
  };

  const handleEditSubmit = e => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('id', editForm.id);
    fd.append('title', editForm.title);
    fd.append('content', editForm.content);
    if (editForm.image) {
      fd.append('image', editForm.image);
    } else if (editPreview && !editPreview.startsWith('blob:')) {
      fd.append('image', editPreview.replace(imgUrl, ''));
    }
    fd.append('status', editForm.status || 'Edited');
    const hospitalId = localStorage.getItem('hospitalCode');
    if (hospitalId) fd.append('hospitalID', hospitalId);
    updateBlog(fd).then(() => {
      setShowEdit(false);
      fetchBlogs();
    });
  };

  const handleEditOpen = blog => {
    setEditForm({
      id: blog.id,
      title: blog.title,
      content: blog.content,
      image: null,
      status: blog.status
    });
    setEditPreview(blog.image ? `${imgUrl}${blog.image}` : null);
    setShowEdit(true);
  };

  const handleDelete = id => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      deleteBlog(id).then(() => fetchBlogs());
    }
  };

  const formatDate = date => date ? new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '-';

  const getTextPreview = html => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // --- RENDERING ---

  if (checkingAccess) {
    return (
      <div className="container-fluid py-5 text-center">
        <i className="fa-solid fa-spinner fa-spin fa-2x text-info"></i>
        <p className="mt-2">Checking blog access permissions...</p>
      </div>
    );
  }

  if (!hasBlogAccess) {
    const userRole = localStorage.getItem('userRole');
    return (
      <div className="container-fluid py-5">
        <div className="alert alert-warning mx-auto text-center" style={{ maxWidth: '600px' }}>
          <i className="fa-solid fa-lock fa-3x mb-3 text-warning"></i>
          <h4>Blog Feature Not Available</h4>
          <p>{userRole?.toLowerCase() === 'admin' 
                ? 'Please subscribe to the Blog Addon Package to use this feature.' 
                : 'You do not have permission to access this page.'}
          </p>
          {userRole?.toLowerCase() === 'admin' && (
            <Button variant="info" href="/#/doctor/plan-selection">View Plans</Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="">
      
      {/* ✅ STEP 1: ADD ROUNDED HEADER & BREADCRUMBS */}
      <div className="page-header filter-bar rounded mb-4">
        <div className="row align-items-center">
          <div className="col">
            <h4 className="page-title">My Blogs</h4>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/doctor/dashboard">Doctor Dashboard</Link>
              </li>
              <li className="breadcrumb-item active">My Blogs</li>
            </ul>
          </div>
          <div className="col-auto">
            <Button 
              onClick={() => setShowAdd(true)} 
              className="btn btn-info rounded-pill py-2 px-4 shadow-sm"
            >
              <i className="fas fa-plus me-2" />
              New Blog
            </Button>
          </div>
        </div>
      </div>
      
      {/* STEP 2: FILTERS */}
      <div className="mb-4 d-flex flex-wrap gap-3">
        <input 
          type="text" 
          className="form-control" 
          style={{ minWidth: 220, maxWidth: 400 }} 
          placeholder="Search blogs..." 
          value={filters.search} 
          onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))} 
        />
        <select 
          className="form-select w-auto" 
          value={filters.status} 
          onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
        >
          <option value="All">All Statuses</option>
          <option value="published">Published</option>
          <option value="created">Created</option>
        </select>
        <select 
          className="form-select w-auto" 
          value={filters.sortBy} 
          onChange={e => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
        >
          <option value="date_desc">Newest First</option>
          <option value="date_asc">Oldest First</option>
          <option value="title_asc">Title A-Z</option>
        </select>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {submitted && <Alert variant="success">Blog saved successfully!</Alert>}
      
      {loading ? (
        <div className="text-center my-5">
          <i className="fa-solid fa-spinner fa-spin fa-3x text-info"></i>
        </div>
      ) : filteredBlogs.length === 0 ? (
        <div className="text-center text-muted my-5">
          <i className="fas fa-blog fa-4x mb-3 text-light"></i>
          <p>No blogs found. Start by creating a new one!</p>
        </div>
      ) : (
        <div className='card'>
        <div className='card-body'>
        <div className="row g-4">
          {filteredBlogs.map(blog => (
            <div key={blog.id} className="col-sm-6 col-md-4 col-lg-3">
              <div className="card h-100 shadow-sm border-0 rounded overflow-hidden">
                {blog.image ? (
                  <img src={`${imgUrl}${blog.image}`} alt="cover" style={{height:'160px', objectFit:'cover'}} className="card-img-top" />
                ) : (
                  <div className="bg-light d-flex justify-content-center align-items-center" style={{height:'160px'}}>
                    <i className="fas fa-image fa-3x text-muted"></i>
                  </div>
                )}
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title text-truncate" title={getTextPreview(blog.title)}>
                    {getTextPreview(blog.title)}
                  </h5>
                  <p className="card-text small text-muted mb-2">
                    {getTextPreview(blog.content).slice(0, 60)}...
                  </p>
                  <div className="mt-auto">
                    <span className={`badge me-2 ${blog.status === 'published' ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {blog.status}
                    </span>
                    <small className="text-muted">{formatDate(blog.createdAt)}</small>
                  </div>
                  <div className="mt-3 d-flex justify-content-end gap-2">
                    <button className="i-btn-view" onClick={() => { setSelectedBlog(blog); setShowView(true); }}></button>
                    <button className="i-btn-edit" onClick={() => handleEditOpen(blog)}></button>
                    <button className="i-btn-delete" onClick={() => handleDeleteClick(blog.id)}></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>
        </div>
      )}
      
      {/* ADD BLOG MODAL */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered size="lg">
        <Modal.Header className='bg-info text-white ' closeButton closeVariant="white"><Modal.Title>Add New Blog</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control type="text" name="title" value={addForm.title} onChange={onAddInputChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Content</Form.Label>
              <ReactQuill theme="snow" value={addForm.content} onChange={val => setAddForm(p => ({ ...p, content: val }))} style={{height: '200px', marginBottom: '50px'}} />
            </Form.Group>
            <Form.Group className="mb-3 mt-5">
              <Form.Label>Cover Image</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={onAddInputChange} name="image" />
              {addPreview && <img src={addPreview} className="img-thumbnail mt-2" style={{maxHeight:'150px'}} alt="preview" />}
            </Form.Group>
            <button type="submit" className="w-100 btn btn-success rounded-pill" disabled={loading}>
              {loading ? <><i className="fa-solid fa-spinner fa-spin me-2"></i>Creating...</> : 'Create Blog'}
            </button>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* EDIT BLOG MODAL */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered size="lg">
        <Modal.Header className='bg-info text-white ' closeButton closeVariant="white"><Modal.Title>Edit Blog</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control type="text" name="title" value={editForm.title} onChange={onEditInputChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Content</Form.Label>
              <ReactQuill theme="snow" value={editForm.content} onChange={val => setEditForm(p => ({ ...p, content: val }))} style={{height: '200px', marginBottom: '50px'}} />
            </Form.Group>
            <Form.Group className="mb-3 mt-5">
              <Form.Label>Change Image</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={onEditInputChange} name="image" />
              {editPreview && <img src={editPreview} className="img-thumbnail mt-2" style={{maxHeight:'150px'}} alt="preview" />}
            </Form.Group>
            <button type="submit" className="w-100 btn btn-info rounded-pill" disabled={loading}>
              {loading ? <><i className="fa-solid fa-spinner fa-spin me-2"></i>Updating...</> : 'Update Blog'}
            </button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* VIEW BLOG MODAL */}
      <Modal show={!!selectedBlog} onHide={() => setSelectedBlog(null)} centered size="lg">
        <Modal.Header className='bg-info text-white ' closeButton closeVariant="white"><Modal.Title>Blog Preview</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedBlog && (
            <div className="p-2">
              {selectedBlog.image && <img src={`${imgUrl}${selectedBlog.image}`} className="img-fluid rounded mb-3 w-100" style={{maxHeight:'300px', objectFit:'cover'}} alt="cover" />}
              <h3>{getTextPreview(selectedBlog.title)}</h3>
              <div className="d-flex gap-2 mb-3">
                <span className="badge bg-info">{selectedBlog.status}</span>
                <span className="text-muted small">{formatDate(selectedBlog.createdAt)}</span>
              </div>
              <hr />
              <div dangerouslySetInnerHTML={{ __html: selectedBlog.content }} />
            </div>
          )}
        </Modal.Body>
      </Modal>
       <MessageModal
        show={deleteModal.show}
        onClose={() => setDeleteModal({ show: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete Blog"
        message="Are you sure you want to delete this blog? This action cannot be undone."
        variant="danger" // This will make the header Red as per your MessageModal logic
        showConfirm={true}
        
      />
    </div>
   
    
  );
};

const mapStateToProps = (state) => ({
  blogState: state.blogReducer,
  accountStatus: state.businessModule?.accountStatus,
});

export default connect(mapStateToProps, {
  listBlogs,
  createBlog,
  deleteBlog,
  updateBlog,
  getAccountStatus,
})(MyBlogs);